import { useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory } from 'react-router-dom';

import '../pm/theme.css';
import { Team, TeamMembership } from '../pm/types';
import {
  createTeam,
  subscribeTeamMembers,
  subscribeTeams,
} from '../pm/firebase';
import { getCurrentUser, readUser } from '../firebase';

const TeamsList: React.FC = () => {
  const history = useHistory();
  const [uid, setUid] = useState<string>('');
  const [isRRAdmin, setIsRRAdmin] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [memberships, setMemberships] = useState<TeamMembership[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then(async (u: any) => {
        if (!active) return;
        setUid(u?.uid || '');
        if (u?.uid) {
          try {
            const data: any = await readUser(u.uid);
            if (!active) return;
            setIsRRAdmin(data?.restricted?.role === 'admin');
          } catch (e) {
            if (active) setIsRRAdmin(false);
          }
        }
        if (active) setAuthReady(true);
      })
      .catch(() => { if (active) setAuthReady(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const unsubT = subscribeTeams(setTeams);
    const unsubM = subscribeTeamMembers(setMemberships);
    return () => { unsubT(); unsubM(); };
  }, []);

  const visibleTeams = useMemo(() => {
    if (isRRAdmin) return teams;
    // Standard users only see teams they belong to
    const myTeamIds = new Set(memberships.filter((m) => m.userId === uid).map((m) => m.teamId));
    return teams.filter((t) => myTeamIds.has(t.id));
  }, [teams, memberships, uid, isRRAdmin]);

  const membersByTeam = useMemo(() => {
    const m: Record<string, TeamMembership[]> = {};
    for (const mb of memberships) {
      if (!m[mb.teamId]) m[mb.teamId] = [];
      m[mb.teamId].push(mb);
    }
    return m;
  }, [memberships]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const id = await createTeam({ name }, uid);
      setName('');
      setShowForm(false);
      history.push(`/Teams/${id}`);
    } catch (err: any) {
      setError(err?.message || 'Could not create team.');
    } finally {
      setBusy(false);
    }
  }

  if (!authReady) {
    return (
      <IonPage><IonContent fullscreen><div className="pm-app"><div className="pm-loading">Loading…</div></div></IonContent></IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page">
            <div className="pm-header">
              <div className="pm-header-titles">
                <h1>Teams</h1>
                <p>{isRRAdmin ? 'All client teams' : 'Teams you belong to'}</p>
              </div>
              {isRRAdmin ? (
                <div className="pm-header-actions">
                  <button
                    className="pm-button pm-button-primary"
                    onClick={() => setShowForm((v) => !v)}
                  >
                    {showForm ? 'Cancel' : 'New team'}
                  </button>
                </div>
              ) : null}
            </div>

            {error ? <div className="pm-error">{error}</div> : null}

            {showForm ? (
              <form className="pm-create-form" onSubmit={handleCreate}>
                <input
                  placeholder="Team name (e.g. SD Treatment)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
                <div>
                  <button
                    className="pm-button pm-button-primary"
                    type="submit"
                    disabled={busy || !name.trim()}
                  >
                    {busy ? 'Creating…' : 'Create team'}
                  </button>
                </div>
              </form>
            ) : null}

            {visibleTeams.length === 0 ? (
              <div className="pm-empty">
                <h3>{isRRAdmin ? 'No teams yet' : "You're not on any teams"}</h3>
                <p>
                  {isRRAdmin
                    ? 'Create a team above to get started.'
                    : 'Ask a Rollover Rep admin to add you to a team.'}
                </p>
              </div>
            ) : (
              <div className="pm-projects-grid">
                {visibleTeams.map((t) => {
                  const memberCount = (membersByTeam[t.id] || []).length;
                  const myMembership = (membersByTeam[t.id] || []).find((m) => m.userId === uid);
                  return (
                    <button
                      key={t.id}
                      className="pm-project-card"
                      onClick={() => history.push(`/Teams/${t.id}`)}
                    >
                      <h3>{t.name}</h3>
                      <div className="pm-project-desc">
                        {memberCount} of {t.seatLimit} seats used
                        {myMembership ? ` · you are ${myMembership.role}` : isRRAdmin ? ' · Rollover Rep view' : ''}
                      </div>
                      <div className="pm-project-progress">
                        <div className="pm-progress-bar">
                          <div
                            className="pm-progress-fill"
                            style={{ width: `${Math.min(100, (memberCount / t.seatLimit) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default TeamsList;
